import { redirect } from '@/helpers/redirects';
import { buildVerifyEmailPath } from '@/apps/verifyEmail/helpers/paths';
import { markEmailVerificationRequired } from '@/apps/verifyEmail/helpers/pendingEmailVerification';
import type { AccessBlocker } from '@/access/types';
import { TRY_AGAIN_ACTION, VERIFY_EMAIL_ACTION } from '@/access/types';

export const DEFAULT_TRY_AGAIN_MESSAGE = 'We couldn’t confirm your email status. Please try again.';

export type ApplyAccessRemediationOptions = {
  returnPath?: string | null;
  beforeRedirect?: () => boolean;
  onPersistenceFailure?: () => void;
  /** Called when remediation is try_again (e.g. verification status unavailable). */
  onTryAgain?: (message: string) => void;
  /** Inline verify UX (e.g. job apply modal) instead of redirecting to /verify-email. */
  onVerifyEmail?: () => boolean;
};

export function resolveVerifyEmailRemediationPath(blockers: AccessBlocker[], returnPath?: string | null): string | null {
  const verifyEmailBlocker = blockers.find((blocker) => blocker.remediation.action === VERIFY_EMAIL_ACTION);

  if (!verifyEmailBlocker) {
    return null;
  }

  return buildVerifyEmailPath(returnPath);
}

export function resolveTryAgainMessage(blockers: AccessBlocker[]): string | null {
  const tryAgainBlocker = blockers.find((blocker) => blocker.remediation.action === TRY_AGAIN_ACTION);
  if (!tryAgainBlocker) {
    return null;
  }

  const serverMessage = tryAgainBlocker.message?.trim();
  return serverMessage || DEFAULT_TRY_AGAIN_MESSAGE;
}

/**
 * Maps backend remediation intents to web-specific navigation or inline retry UX.
 * The API never returns routes — only action names and context — so iOS can implement
 * its own adapter against the same payload.
 */
export function applyAccessRemediation(blockers: AccessBlocker[], options: ApplyAccessRemediationOptions = {}): boolean {
  if (!blockers.length) {
    return false;
  }

  const tryAgainMessage = resolveTryAgainMessage(blockers);
  if (tryAgainMessage) {
    options.onTryAgain?.(tryAgainMessage);
    return true;
  }

  const targetPath = resolveVerifyEmailRemediationPath(blockers, options.returnPath);
  if (!targetPath) {
    return false;
  }

  if (options.beforeRedirect && !options.beforeRedirect()) {
    options.onPersistenceFailure?.();
    return true;
  }

  if (options.onVerifyEmail?.()) {
    return true;
  }

  if (!markEmailVerificationRequired(options.returnPath)) {
    options.onPersistenceFailure?.();
    redirect(targetPath);
    return true;
  }

  redirect(targetPath);
  return true;
}

export function primaryBlockerMessage(blockers: AccessBlocker[]): string | undefined {
  return blockers[0]?.message;
}
